import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTimes } from '@fortawesome/pro-light-svg-icons';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface LocationsSelectionToolbarProps {
  selectedCount: number;
  onClose: () => void;
  onPrintLabels: () => void;
}

export const LocationsSelectionToolbar = memo(
  ({ selectedCount, onClose, onPrintLabels }: LocationsSelectionToolbarProps) => {
    const { t } = useTranslation('locationsModule');

    const handlePrint = useCallback(() => {
      if (selectedCount > 0) {
        onPrintLabels();
      }
    }, [onPrintLabels, selectedCount]);

    if (selectedCount === 0) {
      return null;
    }

    return (
      <div
        className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50"
        data-testid="locations-selection-toolbar"
      >
        <div className="bg-card-bg border border-card-border rounded-lg p-2 shadow-lg">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 bg-primary text-primary-foreground text-sm rounded-full flex items-center justify-center">
                {selectedCount}
              </span>
              <span className="text-sm font-medium text-foreground">{t('selection.selected')}</span>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="border-card-border bg-transparent"
              data-testid="locations-selection-print-labels"
            >
              <FontAwesomeIcon icon={faPrint} className="w-3 h-3 mr-1 text-foreground" />
              {t('selection.print_labels')}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="p-0 hover:bg-transparent"
              aria-label={t('selection.close')}
            >
              <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

LocationsSelectionToolbar.displayName = 'LocationsSelectionToolbar';
